import React, { useState } from 'react';

/**
 * 图片组件，支持错误处理和文件名显示
 * 当图片加载失败时，显示文件名而不是默认图片
 */
const ImageWithFallback = ({ 
  src, 
  alt, 
  className = '', 
  style = {}, 
  showFilenameOnError = true,
  fallbackSrc = null,
  onError = null,
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);

  // 从URL中提取文件名
  const getFilenameFromSrc = (url) => {
    if (!url) return '未知文件';
    try {
      const urlParts = url.split('/');
      const filename = urlParts[urlParts.length - 1];
      return filename || '未知文件';
    } catch (error) {
      return '未知文件';
    }
  };

  const handleImageError = (e) => {
    console.warn('🖼️ 图片加载失败:', src);
    
    // 如果有自定义错误处理函数，先调用它
    if (onError) {
      onError(e);
    }

    // 如果有备用图片且还没尝试过，先尝试备用图片
    if (fallbackSrc && imageSrc !== fallbackSrc) {
      console.log('🔄 尝试备用图片:', fallbackSrc);
      setImageSrc(fallbackSrc);
      return;
    }

    // 如果启用了文件名显示，设置错误状态
    if (showFilenameOnError) {
      setImageError(true);
    }
  };

  const handleImageLoad = () => {
    // 图片加载成功，重置错误状态
    setImageError(false);
  };

  // 如果图片加载失败且启用了文件名显示，显示文件名
  if (imageError && showFilenameOnError) {
    const filename = getFilenameFromSrc(src);
    
    return (
      <div 
        className={`flex flex-col items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-4 ${className}`}
        style={style}
        {...props}
      >
        {/* 图片图标 */}
        <div className="text-gray-400 mb-2">
          <svg 
            className="w-12 h-12" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
        </div>
        
        {/* 文件名显示 */}
        <div className="text-center">
          <p className="text-sm font-medium text-gray-600 mb-1">图片未找到</p>
          <p className="text-xs text-gray-500 font-mono bg-gray-200 px-2 py-1 rounded">
            {filename}
          </p>
        </div>
        
        {/* 重试按钮 */}
        <button
          onClick={() => {
            setImageError(false);
            setImageSrc(src + '?retry=' + Date.now()); // 添加时间戳强制重新加载
          }}
          className="mt-2 text-xs text-blue-500 hover:text-blue-700 underline"
        >
          重试加载
        </button>
      </div>
    );
  }

  // 正常显示图片
  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      onError={handleImageError}
      onLoad={handleImageLoad}
      {...props}
    />
  );
};

export default ImageWithFallback;
